
import { parseEther, formatEther } from "viem";
import { useAccount, useBalance, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { polygon } from "viem/chains";
import { useNetworkSwitch } from "@/components/create-coin/form/hooks/useNetworkSwitch";

// Import the compiled LaunchPool configuration
// Note: This file will be generated after running: node src/contracts/deploy-launchpool.js
import { FAIR_LAUNCH_POOL_ABI as LAUNCHPOOL_ABI, FAIR_LAUNCH_POOL_ADDRESS as LAUNCHPOOL_CONTRACT } from './FairLaunchPoolConfig';

// Fallback ABI if config not generated yet
const FALLBACK_ABI = [
  {
    inputs: [{ internalType: "address", name: "tokenAddress", type: "address" }],
    name: "getCurrentPrice",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "tokenAddress", type: "address" }],
    name: "getRemainingTime",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "tokenAddress", type: "address" }],
    name: "getTotalCommitment",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "tokenAddress", type: "address" }],
    name: "getParticipantCount",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [{ internalType: "address", name: "tokenAddress", type: "address" }],
    name: "commit",
    outputs: [],
    stateMutability: "payable",
    type: "function",
  },
] as const;

// Use compiled ABI or fallback
const ABI = LAUNCHPOOL_ABI && LAUNCHPOOL_ABI.length > 0 ? LAUNCHPOOL_ABI : FALLBACK_ABI;

// Use contract address from config or undefined
const CONTRACT_ADDRESS = LAUNCHPOOL_CONTRACT;

export interface LaunchPoolData {
  currentPrice: string;
  timeRemaining: number;
  totalCommitment: string;
  targetCommitment: string;
  participantCount: number;
  loading: boolean;
  startPrice: string;
  endPrice: string;
}

export function useLaunchPool(tokenAddress: string) {
  const { toast } = useToast();
  const { address } = useAccount();
  const { ensureCorrectNetwork } = useNetworkSwitch();
  const [isNetworkCorrect, setIsNetworkCorrect] = useState(false);
  
  // State for the commitment amount
  const [commitment, setCommitment] = useState<string>("");
  
  // Get user's MATIC balance
  const { data: balance } = useBalance({
    address,
    chainId: polygon.id,
  });

  // Contract read for current price
  const { data: currentPrice, isError: isPriceError, isPending: isPriceLoading } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: ABI,
    functionName: "getCurrentPrice",
    args: [tokenAddress as `0x${string}`],
    chainId: polygon.id,
    query: { enabled: !!CONTRACT_ADDRESS },
  });

  // Contract read for remaining time
  const { data: timeRemaining, isError: isTimeError, isPending: isTimeLoading } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: ABI,
    functionName: "getRemainingTime",
    args: [tokenAddress as `0x${string}`],
    chainId: polygon.id,
    query: { enabled: !!CONTRACT_ADDRESS },
  });

  // Contract read for total commitment
  const { data: totalCommitment, isError: isCommitmentError, isPending: isCommitmentLoading } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: ABI,
    functionName: "getTotalCommitment",
    args: [tokenAddress as `0x${string}`],
    chainId: polygon.id,
    query: { enabled: !!CONTRACT_ADDRESS },
  });

  // Contract read for participant count
  const { data: participantCount, isError: isCountError, isPending: isCountLoading } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: ABI,
    functionName: "getParticipantCount",
    args: [tokenAddress as `0x${string}`],
    chainId: polygon.id,
    query: { enabled: !!CONTRACT_ADDRESS },
  });

  const { data: poolConfig } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: ABI,
    functionName: "pools",
    args: [tokenAddress as `0x${string}`],
    chainId: polygon.id,
    query: { enabled: !!CONTRACT_ADDRESS },
  });

  // Contract write for committing MATIC
  const { writeContract, isPending: isCommitting, isError: isCommitError, data: txHash } = useWriteContract();
  const { writeContract: finalizeWrite, isPending: isFinalizing } = useWriteContract();
  const { writeContract: claimWrite, isPending: isClaiming } = useWriteContract();

  // Wait for transaction receipt
  const { isLoading: isWaitingForTx, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Check network on component mount
  useEffect(() => {
    const checkNetwork = async () => {
      const networkCorrect = await ensureCorrectNetwork();
      setIsNetworkCorrect(networkCorrect);
    };
    
    checkNetwork();
  }, [ensureCorrectNetwork]);

  // Handle transaction success
  useEffect(() => {
    if (isTxSuccess) {
      toast({
        title: "Commitment successful",
        description: `Successfully committed ${commitment} MATIC to the launch pool`,
      });
      setCommitment("");
    }
  }, [isTxSuccess, commitment, toast]);

  // Check if LaunchPool contract is deployed
  const isContractDeployed = CONTRACT_ADDRESS && CONTRACT_ADDRESS !== "0x0000000000000000000000000000000000000000";

  // Handle commit function
  const handleCommit = async () => {
    if (!isContractDeployed) {
      toast({
        title: "LaunchPool Not Available",
        description: "LaunchPool contract has not been deployed yet. Please deploy it first.",
        variant: "destructive",
      });
      return;
    }

    if (!isNetworkCorrect) {
      const networkCorrect = await ensureCorrectNetwork();
      if (!networkCorrect) return;
    }

    const amount = parseFloat(commitment);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount to commit",
        variant: "destructive"
      });
      return;
    }

    // Check if user has enough balance
    if (balance && parseEther(commitment) > balance.value) {
      toast({
        title: "Insufficient balance",
        description: `You don't have enough MATIC. Your balance: ${balance.formatted} MATIC`,
        variant: "destructive"
      });
      return;
    }

    try {
      writeContract({
        account: address,
        chain: polygon,
        address: CONTRACT_ADDRESS as `0x${string}`,
        abi: ABI,
        functionName: "commit",
        args: [tokenAddress as `0x${string}`],
        value: parseEther(commitment),
      });
    } catch (error) {
      console.error("Error committing to launch pool:", error);
      toast({
        title: "Transaction failed",
        description: "Failed to commit MATIC to the launch pool",
        variant: "destructive"
      });
    }
  };

  const handleFinalize = () => finalizeWrite({ address: CONTRACT_ADDRESS as `0x${string}`, abi: ABI, functionName: "finalize", args: [tokenAddress as `0x${string}`], chain: polygon } as any);
  const handleClaim = () => claimWrite({ address: CONTRACT_ADDRESS as `0x${string}`, abi: ABI, functionName: "claim", args: [tokenAddress as `0x${string}`], chain: polygon } as any);

  // Mock values used as fallbacks if contract data is not available
  const mockData = {
    startPrice: "0.01",
    endPrice: "0.001",
    targetCommitment: "20000",
  };

  // Fixed: Properly type-check and convert contract data
  const launchPoolData: LaunchPoolData = {
    currentPrice: currentPrice ? formatEther(currentPrice as bigint) : mockData.startPrice,
    timeRemaining: timeRemaining ? Number(timeRemaining) : 3600, // 1 hour in seconds
    totalCommitment: totalCommitment ? formatEther(totalCommitment as bigint) : "0",
    targetCommitment: poolConfig ? formatEther((poolConfig as any)[7]) : mockData.targetCommitment,
    participantCount: participantCount ? Number(participantCount) : 0,
    loading: isPriceLoading || isTimeLoading || isCommitmentLoading || isCountLoading || isCommitting || isWaitingForTx,
    startPrice: poolConfig ? formatEther((poolConfig as any)[3]) : mockData.startPrice,
    endPrice: poolConfig ? formatEther((poolConfig as any)[4]) : mockData.endPrice,
  };

  const isError = isPriceError || isTimeError || isCommitmentError || isCountError || isCommitError;
  
  if (isError) {
    console.error("Error fetching launch pool data");
  }

  return {
    launchPoolData,
    commitment,
    setCommitment,
    handleCommit,
    isCommitting: isCommitting || isWaitingForTx,
    isError,
    isFinalized: Boolean(poolConfig && (poolConfig as any)[10]),
    handleFinalize,
    handleClaim,
    isFinalizing,
    isClaiming,
  };
}
